import circlePlusIcon from "@tabler/icons/outline/circle-plus.svg";
import editIcon from "@tabler/icons/outline/edit.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useMatch } from "react-router-dom";

import { useGroupLookup } from "@/api/groups";
import { Avatar, HStack, Icon } from "@/components/ui";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { cn } from "@/utils/cn";

const messages = defineMessages({
  compose: { id: "fab.compose", defaultMessage: "Compose Truth" },
  create_group: { id: "fab.create_group", defaultMessage: "Create Group" },
});

/** FloatingActionButton (aka FAB), a composer button that floats in the corner on mobile. */
const FloatingActionButton: React.FC = () => {
  const location = useLocation();

  if (location.pathname.startsWith("/group/")) {
    return <GroupFAB />;
  }

  if (location.pathname.startsWith("/groups")) {
    return <CreateGroupFAB />;
  }

  return <HomeFAB />;
};

interface BaseFABProps {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

const BaseFAB: React.FC<BaseFABProps> = ({ onClick, label, children }) => (
  <button
    onClick={onClick}
    className={cn(
      "inline-flex appearance-none items-center rounded-full border p-4 font-medium transition-all focus:ring-2 focus:ring-offset-2 focus:outline-hidden",
      "border-transparent bg-secondary-500 text-gray-100 hover:bg-secondary-400 focus:bg-secondary-500 focus:ring-secondary-400",
    )}
    aria-label={label}
  >
    {children}
  </button>
);

const HomeFAB: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  return (
    <BaseFAB
      onClick={() => openModal("COMPOSE")}
      label={intl.formatMessage(messages.compose)}
    >
      <Icon src={editIcon} className="size-6" />
    </BaseFAB>
  );
};

const GroupFAB: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModalContext();
  const match = useMatch("/group/:groupSlug");
  const { group } = useGroupLookup(match?.params.groupSlug || "");
  const { updateComposer } = useComposer(`group:${group?.id}`);
  const composeId = `group:${group?.id}`;

  if (!group) return null;

  const handleOpenComposeModal = () => {
    updateComposer({ groupId: group.id });

    openModal("COMPOSE", {
      composeId,
      audienceReadable: true,
      groupId: group.id,
    });
  };

  return (
    <BaseFAB
      onClick={handleOpenComposeModal}
      label={intl.formatMessage(messages.compose)}
    >
      <HStack space={3} alignItems="center">
        <Avatar
          className="-my-3 -ml-2 border-white"
          size={42}
          src={group.avatar_static}
        />
        <Icon src={editIcon} className="size-6" />
      </HStack>
    </BaseFAB>
  );
};

const CreateGroupFAB: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  return (
    <BaseFAB
      onClick={() => openModal("CREATE_GROUP")}
      label={intl.formatMessage(messages.create_group)}
    >
      <Icon src={circlePlusIcon} className="size-6" />
    </BaseFAB>
  );
};

export default FloatingActionButton;
